import { inject, Injectable, signal } from '@angular/core';
import { Environment, ENVIRONMENT, ToastData, ToastType } from '@ld.consumer-portal/models';

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private readonly environment = inject<Environment>(ENVIRONMENT);

  private readonly DEFAULT_DURATION = 8000;

  private readonly MAX_TOASTS = 5;

  private readonly _toasts = signal<ToastData[]>([]);
  readonly toasts = this._toasts.asReadonly();

  private idCounter = 0;

  success(message: string, header?: string, duration?: number, actionLabel?: string, actionCallback?: () => void): void {
    this.show(ToastType.Success, message, header, duration, actionLabel, actionCallback);
  }

  error(message: string, header?: string, duration?: number, actionLabel?: string, actionCallback?: () => void): void {
    this.show(ToastType.Error, message, header, duration, actionLabel, actionCallback);
  }

  warning(message: string, header?: string, duration?: number, actionLabel?: string, actionCallback?: () => void): void {
    this.show(ToastType.Warning, message, header, duration, actionLabel, actionCallback);
  }

  info(message: string, header?: string, duration?: number, actionLabel?: string, actionCallback?: () => void): void {
    this.show(ToastType.Info, message, header, duration, actionLabel, actionCallback);
  }

  dismiss(id: string): void {
    this._toasts.update((toasts) => toasts.filter((t) => t.id !== id));
  }

  dismissAll(): void {
    this._toasts.set([]);
  }

  private show(
    type: ToastType,
    message: string,
    header?: string,
    duration?: number,
    actionLabel?: string,
    actionCallback?: () => void,
  ): void {
    const id = `toast-${++this.idCounter}`;
    const toastDuration = duration ?? this.DEFAULT_DURATION;

    const toast: ToastData = {
      id,
      type,
      message,
      header,
      duration: toastDuration,
      dismissible: true,
      actionLabel,
      actionCallback,
    };

    this._toasts.update((toasts) => {
      const updated = [...toasts, toast];
      if (updated.length > this.MAX_TOASTS) {
        return updated.slice(-this.MAX_TOASTS);
      }
      return updated;
    });

    if (toastDuration > 0) {
      setTimeout(() => this.dismiss(id), toastDuration);
    }
  }
}
